import React, { useState, useCallback, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCollectionsStore } from '@/stores/collectionsStore';
import type { Collection, CreateCollection, CreateCollectionQuery, CreateFolder } from '@/services/api';
import { COLLECTION_EVENTS, dispatchCollectionEvent } from '@/utils/collectionEvents';

interface CollectionManagerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create-collection' | 'create-query' | 'edit-collection' | 'create-folder';
  collection?: Collection;
  collectionId?: string;
  parentFolderId?: string;
  initialQuery?: {
    name: string;
    query: string;
    description?: string;
    databaseName?: string;
    schemaName?: string;
  };
  tabId?: string; // ID of the tab being saved
}

export const CollectionManager: React.FC<CollectionManagerProps> = ({
  isOpen,
  onClose,
  mode,
  collection,
  collectionId,
  parentFolderId,
  initialQuery,
  tabId
}) => {
  const { createCollection, updateCollection, createQuery, createFolder, collections, isLoading } = useCollectionsStore();
  
  // Local error state for form display
  const [formError, setFormError] = useState<string | null>(null);
  
  // Collection form state
  const [collectionForm, setCollectionForm] = useState({
    name: collection?.name || '',
    description: collection?.description || '',
    parentCollectionId: collection?.parentCollectionId || ''
  });

  // Query form state
  const [queryForm, setQueryForm] = useState({
    name: initialQuery?.name || '',
    description: initialQuery?.description || '',
    query: initialQuery?.query || '',
    tags: [] as string[],
    databaseName: initialQuery?.databaseName || '',
    schemaName: initialQuery?.schemaName || '',
    collectionId: collectionId || ''
  });

  // Folder form state
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: '',
    parentFolderId: parentFolderId || ''
  });

  const [tagInput, setTagInput] = useState('');

  // Clear error when modal opens or mode changes, and reset forms when modal closes
  useEffect(() => {
    if (isOpen) {
      setFormError(null);
      
      // When opening in create-query mode with a specific collection, set it immediately
      if (mode === 'create-query') {
        setQueryForm({
          name: initialQuery?.name || '',
          description: initialQuery?.description || '',
          query: initialQuery?.query || '',
          tags: [],
          databaseName: initialQuery?.databaseName || '',
          schemaName: initialQuery?.schemaName || '',
          collectionId: collectionId || ''  // Use the passed collectionId
        });
      }
      
      // When opening in create-folder mode with a specific parent folder, set it immediately
      if (mode === 'create-folder') {
        setFolderForm({
          name: '',
          description: '',
          parentFolderId: parentFolderId || ''
        });
      }
      
      // When opening in edit-collection mode, set collection form
      if (mode === 'edit-collection' && collection) {
        setCollectionForm({
          name: collection.name || '',
          description: collection.description || '',
          parentCollectionId: collection.parentCollectionId || ''
        });
      }
    } else {
      // Reset forms when modal closes
      setQueryForm({
        name: '',
        description: '',
        query: '',
        tags: [],
        databaseName: '',
        schemaName: '',
        collectionId: ''
      });
      setFolderForm({
        name: '',
        description: '',
        parentFolderId: ''
      });
      setCollectionForm({
        name: '',
        description: '',
        parentCollectionId: ''
      });
      setTagInput('');
    }
  }, [isOpen, mode, collectionId, initialQuery, parentFolderId, collection]);

  const handleCollectionSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null); // Clear any previous errors
    
    void (async () => {
      try {
        const data: CreateCollection = {
          name: collectionForm.name,
          description: collectionForm.description || undefined,
          parentCollectionId: collectionForm.parentCollectionId || undefined
        };

        if (mode === 'create-collection') {
          await createCollection(data);
        } else if (mode === 'edit-collection' && collection?.id) {
          await updateCollection(collection.id, data);
        }
        
        // Only close on success
        onClose();
      } catch (error) {
        // Display error in the form
        const errorMessage = error instanceof Error ? error.message : 'Failed to save collection';
        setFormError(errorMessage);
        console.error('Failed to save collection:', error);
      }
    })();
  }, [collectionForm, mode, createCollection, updateCollection, collection?.id, onClose]);

  const handleQuerySubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!queryForm.collectionId) return;
    setFormError(null); // Clear any previous errors

    void (async () => {
      try {
        const data: CreateCollectionQuery = {
          name: queryForm.name,
          description: queryForm.description || undefined,
          query: queryForm.query,
          tags: queryForm.tags.length > 0 ? queryForm.tags : undefined,
          databaseName: queryForm.databaseName || undefined,
          schemaName: queryForm.schemaName || undefined
        };

        const queryId = await createQuery(queryForm.collectionId, data);
        
        // Dispatch event to notify CollectionTree components
        if (queryId) {
          dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_CREATED, {
            collectionId: queryForm.collectionId,
            queryId,
            tabId: tabId,
            queryName: queryForm.name
          });
        }
        
        // Only close on success
        onClose();
      } catch (error) {
        // Display error in the form
        const errorMessage = error instanceof Error ? error.message : 'Failed to save query';
        setFormError(errorMessage);
        console.error('Failed to save query:', error);
      }
    })();
  }, [queryForm, createQuery, tabId, onClose]);

  const handleFolderSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!collectionId) return;
    setFormError(null); // Clear any previous errors

    void (async () => {
      try {
        const data: CreateFolder = {
          name: folderForm.name,
          description: folderForm.description || undefined,
          parentFolderId: folderForm.parentFolderId || undefined
        };

        const folderId = await createFolder(collectionId, data);
        
        // Dispatch event to notify other components immediately
        if (folderId) {
          dispatchCollectionEvent(COLLECTION_EVENTS.FOLDER_CREATED, {
            collectionId,
            folderId,
            folderName: folderForm.name
          });
        }
        
        // Only close on success
        onClose();
      } catch (error) {
        // Display error in the form
        const errorMessage = error instanceof Error ? error.message : 'Failed to create folder';
        setFormError(errorMessage);
        console.error('Failed to create folder:', error);
      }
    })();
  }, [folderForm, createFolder, collectionId, onClose]);

  const addTag = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!queryForm.tags.includes(newTag)) {
        setQueryForm(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      setTagInput('');
    }
  }, [tagInput, queryForm.tags]);

  const removeTag = useCallback((tagToRemove: string) => {
    setQueryForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  }, []);

  const handleRemoveTag = useCallback((tagToRemove: string) => () => {
    removeTag(tagToRemove);
  }, [removeTag]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" role="dialog" data-testid="collection-manager">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {mode === 'create-collection' && 'Create Collection'}
            {mode === 'edit-collection' && 'Edit Collection'}
            {mode === 'create-query' && 'Save Query to Collection'}
            {mode === 'create-folder' && 'Create Folder'}
          </h2>
          <button type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Collection Form */}
        {(mode === 'create-collection' || mode === 'edit-collection') && (
          <form onSubmit={handleCollectionSubmit} className="space-y-4">
            {/* Error display */}
            {formError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400 dark:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{formError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Collection Name
              </label>
              <input
                type="text"
                value={collectionForm.name}
                onChange={(e) => { setCollectionForm(prev => ({ ...prev, name: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter collection name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={collectionForm.description}
                onChange={(e) => { setCollectionForm(prev => ({ ...prev, description: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter description (optional)"
                rows={3}
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Parent Collection
              </label>
              <select
                value={collectionForm.parentCollectionId}
                onChange={(e) => { setCollectionForm(prev => ({ ...prev, parentCollectionId: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">None (Root Level)</option>
                {collections.map((col) => (
                  <option key={col.id} value={col.id || ''}>
                    {col.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : (mode === 'create-collection' ? 'Create' : 'Update')}
              </button>
            </div>
          </form>
        )}

        {/* Query Form */}
        {mode === 'create-query' && (
          <form onSubmit={handleQuerySubmit} className="space-y-4">
            {/* Error display */}
            {formError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400 dark:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{formError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Collection
              </label>
              <select
                value={queryForm.collectionId}
                onChange={(e) => { setQueryForm(prev => ({ ...prev, collectionId: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                required
              >
                <option value="">Select a collection</option>
                {collections.map((col) => (
                  <option key={col.id} value={col.id || ''}>
                    {col.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Query Name
              </label>
              <input
                type="text"
                value={queryForm.name}
                onChange={(e) => { setQueryForm(prev => ({ ...prev, name: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter query name"
                required
                maxLength={70}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={queryForm.description}
                onChange={(e) => { setQueryForm(prev => ({ ...prev, description: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter description (optional)"
                rows={2}
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                SQL Query
              </label>
              <textarea
                value={queryForm.query}
                onChange={(e) => { setQueryForm(prev => ({ ...prev, query: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                placeholder="Enter SQL query"
                rows={6}
                required
                maxLength={5000}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Database
                </label>
                <input
                  type="text"
                  value={queryForm.databaseName}
                  onChange={(e) => { setQueryForm(prev => ({ ...prev, databaseName: e.target.value })) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Database name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Schema
                </label>
                <input
                  type="text"
                  value={queryForm.schemaName}
                  onChange={(e) => { setQueryForm(prev => ({ ...prev, schemaName: e.target.value })) }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Schema name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tags
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => { setTagInput(e.target.value) }}
                  onKeyDown={addTag}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Type a tag and press Enter"
                />
                {queryForm.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {queryForm.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-medium rounded-full"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={handleRemoveTag(tag)}
                          className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save Query'}
              </button>
            </div>
          </form>
        )}

        {/* Folder Form */}
        {mode === 'create-folder' && (
          <form onSubmit={handleFolderSubmit} className="space-y-4">
            {/* Error display */}
            {formError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400 dark:text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{formError}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                value={folderForm.name}
                onChange={(e) => { setFolderForm(prev => ({ ...prev, name: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter folder name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={folderForm.description}
                onChange={(e) => { setFolderForm(prev => ({ ...prev, description: e.target.value })) }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Enter description (optional)"
                rows={3}
                maxLength={100}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};